// admin_service.js — FINAL COMPLETE VERSION
// Replace your entire src/services/admin_service.js with this file

import API from "./api";

const AdminService = {
  // ═══════════════════════════════════════════════════════════════════════
  // 📊 1. DASHBOARD STATS
  // ═══════════════════════════════════════════════════════════════════════
  
  getDashboardStats: async () => {
    try {
      const res = await API.get("/admin/stats");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching dashboard stats";
    }
  },
 
  getRecentTickets: async (limit = 10) => {
    try {
      const res = await API.get(`/admin/recent-tickets?limit=${limit}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching recent tickets";
    }
  },
 
  getTicketsByStatus: async () => {
    try {
      const res = await API.get("/admin/tickets-by-status");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching ticket status breakdown";
    }
  },
 
  getCategoryWorkload: async () => {
    try {
      const res = await API.get("/admin/category-workload");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching category workload";
    }
  },
 
  getHodWorkload: async () => {
    try {
      const res = await API.get("/admin/hod-workload");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching HOD workload";
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 🎓 2. HOD MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════
  
  getAllHods: async () => {
    try {
      const res = await API.get("/admin/hods");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching HODs";
    }
  },

  createHod: async (formData) => {
    try {
      const res = await API.post("/admin/create-hod", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error creating HOD";
    }
  },

  deleteHod: async (id) => {
    try {
      const res = await API.delete(`/admin/hod/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error deleting HOD";
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 📂 3. CATEGORY MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════
  
  getAllCategories: async () => {
    try {
      const res = await API.get("/admin/categories");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching categories";
    }
  },

  getCategories: async () => {
    try {
      const res = await API.get("/admin/categories");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching categories";
    }
  },

  createCategory: async (formData) => {
    try {
      const res = await API.post("/admin/create-category", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error adding category";
    }
  },

  updateCategory: async (id, formData) => {
    try {
      const res = await API.put(`/admin/category/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error updating category";
    }
  },

  deleteCategory: async (id) => {
    try {
      const res = await API.delete(`/admin/category/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error deleting category";
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 📑 4. SUB-CATEGORY MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════
  
  getSubCategories: async (categoryId) => {
    try {
      const res = await API.get(`/admin/sub-categories/${categoryId}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching sub-categories";
    }
  },

  createSubCategory: async (subData) => {
    try {
      const res = await API.post("/admin/create-sub-category", subData);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error adding sub-category";
    }
  },

  updateSubCategory: async (id, data) => {
    try {
      const res = await API.put(`/admin/sub-category/${id}`, data);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error updating sub-category";
    }
  },

  deleteSubCategory: async (id) => {
    try {
      const res = await API.delete(`/admin/sub-category/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error deleting sub-category";
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 🎫 5. TICKET MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════
  
  getGlobalTickets: async () => {
    try {
      const res = await API.get("/admin/all-tickets");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching global tickets";
    }
  },

  // 🔍 SEARCH TICKETS WITH FILTERS
  searchTickets: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.searchTerm) params.append("searchTerm", filters.searchTerm);
      if (filters.status) params.append("status", filters.status);
      if (filters.category) params.append("category", filters.category);
      if (filters.dateFrom) params.append("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.append("dateTo", filters.dateTo);
      if (filters.rating) params.append("rating", filters.rating);
      if (filters.sort) params.append("sort", filters.sort);
      if (filters.order) params.append("order", filters.order);

      const res = await API.get(`/admin/search-tickets?${params.toString()}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error searching tickets";
    }
  },

  // 📋 GET SINGLE TICKET FULL DETAILS
  getTicketDetails: async (id) => {
    try {
      const res = await API.get(`/admin/ticket/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching ticket details";
    }
  },

  // 📊 GET FILTER OPTIONS FOR DROPDOWNS
  getTicketFilterOptions: async () => {
    try {
      const res = await API.get("/admin/ticket-filters");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching filter options";
    }
  },

  // 👑 ADMIN OVERRIDE TICKET STATUS (Shahid Sir Point 3)
  updateTicketStatusByAdmin: async (ticketId, status, remarks = "") => {
    try {
      const res = await API.put("/admin/update-ticket-status", { ticketId, status, remarks });
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error updating ticket status";
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 👥 6. USER MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════
  
  getAllUsers: async () => {
    try {
      const res = await API.get("/admin/users");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching user directory";
    }
  },

  createUser: async (formData) => {
    try {
      const res = await API.post("/admin/create-user", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error creating user";
    }
  },

  changeUserPassword: async ({ userId, newPassword }) => {
    try {
      const res = await API.put("/admin/change-user-password", { userId, newPassword });
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error updating password";
    }
  },

  deleteUser: async (id) => {
    try {
      const res = await API.delete(`/admin/user/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error terminating user session";
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // ⚙️ 7. ADMIN SETTINGS & PROFILE
  // ═══════════════════════════════════════════════════════════════════════
  
  getAdminProfile: async () => {
    try {
      const res = await API.get("/admin/profile");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching profile";
    }
  },

  updateAdminProfile: async (formData) => {
    try {
      const res = await API.put("/admin/update-profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error updating profile";
    }
  },

  changeAdminPassword: async ({ oldPassword, newPassword }) => {
    try {
      const res = await API.put("/admin/change-password", { oldPassword, newPassword });
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error changing password";
    }
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 📍 8. LOCATION & QR HUB
  // ═══════════════════════════════════════════════════════════════════════
  
  getAllLocations: async () => {
    try {
      const res = await API.get("/admin/locations");
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error fetching locations";
    }
  },

  addLocation: async (locData) => {
    try {
      const res = await API.post("/admin/add-location", locData);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error registering location";
    }
  },

  deleteLocation: async (id) => {
    try {
      const res = await API.delete(`/admin/location/${id}`);
      return res.data;
    } catch (err) {
      throw err.response?.data || "Error deleting location";
    }
  },
};

export default AdminService;